import { useEffect, useRef, useState } from "react";

/**
 * Renders Google's own "Sign in with Google" button via Google Identity
 * Services (no npm package needed — the script is loaded on demand).
 *
 * This is only ever mounted when Platform Settings has Google sign-in
 * turned on AND a Client ID is configured (see the `clientId` prop, which
 * callers get from GET /api/settings) — that's the admin-controlled
 * on/off switch described in the task: leave it off and this component
 * simply never renders anything, no broken button, no console errors.
 */
export default function GoogleSignInButton({
  clientId,
  onIdToken,
  onError,
  text = "continue_with",
}) {
  const containerRef = useRef(null);
  const [scriptReady, setScriptReady] = useState(
    typeof window !== "undefined" && !!window.google?.accounts?.id
  );

  useEffect(() => {
    if (!clientId || scriptReady) return;

    const existing = document.getElementById("google-identity-script");
    if (existing) {
      existing.addEventListener("load", () => setScriptReady(true), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = "google-identity-script";
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = () => setScriptReady(true);
    script.onerror = () => onError?.(new Error("Failed to load Google sign-in"));
    document.head.appendChild(script);
    // onError intentionally excluded — only the clientId identity matters here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, scriptReady]);

  useEffect(() => {
    if (!scriptReady || !clientId || !containerRef.current || !window.google?.accounts?.id) {
      return;
    }

    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: (response) => {
        if (response?.credential) {
          onIdToken(response.credential);
        } else {
          onError?.(new Error("Google sign-in did not return a credential"));
        }
      },
    });

    window.google.accounts.id.renderButton(containerRef.current, {
      type: "standard",
      theme: "outline",
      size: "large",
      shape: "pill",
      width: 320,
      text,
    });
    // onIdToken/onError intentionally excluded to avoid re-rendering the
    // Google button on every parent re-render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scriptReady, clientId, text]);

  if (!clientId) return null;

  return <div ref={containerRef} className="flex justify-center w-full" />;
}
